<template>
  <div class="index-page page">
    <div class="title">{{ $t('tableOfContent') }}</div>

    <div v-for="site in index" :key="site.id" class="site-container">
      <div class="site-line line">
        <div class="site-title">{{ site.name === '0xa1e415' ? $t('sections.mainSynthesis') : site.name }}</div>
        <div class="space-line"></div>
        <div class="index-page-number">{{ site.page }}</div>
      </div>

      <div v-for="line in site.lines" :key="line.id" class="index-line line">
        <div class="line-title">{{ line.name }}</div>
        <div class="space-line"></div>
        <div class="index-page-number">{{ line.page }}</div>
      </div>

      <div v-if="site.transports" class="transport-container">
        <div v-for="transport in site.transports" :key="transport.id" class="index-container">
          <div class="icon-container" :class="`${transport.icon}`">
            <i v-if="transport.icon === 'walking'" class="material-icons">directions_walk</i>
            <i v-else :class="[`icon-${transport.icon}`]" />
          </div>
          <div class="index-content">
            <div class="transport-line line">
              <div class="line-title">{{ $t(`transportMode.${transport.name}`) }}</div>
              <div class="space-line"></div>
              <div class="index-page-number">{{ transport.page }}</div>
            </div>
            <div v-for="section in transport.sections" :key="section.id" class="line">
              <div class="line-title">{{ $t(`sections.${section.name}`) }}</div>
              <div class="space-line"></div>
              <div class="index-page-number">{{ section.page }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="footer">
      <div class="focus" :class="{ show: filters }">{{ $t('focusStudyOn') }} : {{ filters }}</div>
      <div class="right-container">
        <div class="logo-line">
          <div class="jll-logo"></div>
          <div class="divider-vertical"></div>
          <div class="commuter-logo">Commuter</div>
        </div>
        <div class="page-number">
          {{ currentPage }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';

export default {
  name: 'IndexPage',

  props: {
    index: {
      type: Array,
      required: false,
      default: () => [],
    },

    study: {
      type: Object,
      required: false,
      default: () => ({}),
    },

    export: {
      type: Object,
      required: false,
      default: () => ({}),
    },

    filters: {
      type: String,
      required: false,
      default: null,
    },
  },

  data() {
    return {
      currentPage: 0,
    };
  },

  computed: {
    ...mapGetters({
      totalPages: 'totalPages',
    }),
  },

  mounted() {
    this.currentPage = Number(this.totalPages + 1);
    this.$store.dispatch('setPage');

    this.$store.dispatch('setIndexPage', this.currentPage);
  },
};
</script>

<style scoped lang="scss">
@use '@/styles/colors.scss';

.index-page {
  position: relative;
  flex: 1;
  overflow: auto;
  padding: 48px 80px;
  box-sizing: border-box;

  .title {
    display: flex;
    flex-direction: column;
    font-size: 32px;
    color: #3c3c3b;
    margin-bottom: 16px;

    &::after {
      content: '';
      margin: 8px 0;
      width: 64px;
      height: 4px;
      border-radius: 4px;
      background: colors.$main-color;
    }
  }

  .site-container {
    width: 100%;
    margin: 0 0 24px 0;

    .line {
      display: flex;
      align-items: baseline;
      margin: 0 0 4px;

      .line-title {
        flex-shrink: 0;
        font-size: 14px;
        color: #77797f;
      }
      .space-line {
        flex-grow: 1;
        width: 100%;
        height: 1px;
        border-radius: 2px;
        margin: 0 6px;
        background: #d7d5d3;
      }
      .index-page-number {
        flex-shrink: 0;
        font-size: 16px;
        color: #3c3c3b;
      }

      &.transport-line,
      &.index-line {
        margin: 0 0 8px;

        .line-title {
          font-size: 18px;
          font-weight: 600;
        }
        .index-page-number {
          font-size: 20px;
        }
      }

      &.index-line {
        margin: 0 0 8px 48px;
      }
    }

    .site-line {
      display: flex;
      align-items: baseline;
      color: #3c3c3b;

      .site-title {
        flex-shrink: 0;
        font-size: 24px;
        font-weight: bold;
      }
      .space-line {
        flex-grow: 1;
        width: 100%;
        height: 1px;
        border-radius: 2px;
        margin: 0 12px;
        background: #3c3c3b;
      }
      .index-page-number {
        flex-shrink: 0;
        font-size: 24px;
        font-weight: bold;
      }
    }

    .transport-container {
      display: flex;
      flex-wrap: wrap;
      margin: 24px 0 0;

      .index-container {
        display: flex;
        width: 47%;
        margin: 0 3% 24px 0;

        &:nth-child(even) {
          margin: 0 0 0 3%;
        }

        .icon-container {
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          $size: 32px;
          width: $size;
          height: $size;
          border-radius: $size;
          line-height: $size;
          text-align: center;
          font-size: 24px;
          margin: 0 8px 0 0;
          color: colors.$white-color;
          background: colors.$red-color;

          .material-icons {
            margin: 0;
          }

          &.bus {
            background: #d60808;
          }
          &.car {
            background: #45d2b2;
          }
          &.people_star {
            background: #602376;
          }
          &.cycle {
            background: #3385a1;
          }
          &.chrono {
            background: #f5a623;
          }
          &.walking {
            background: #228b22;
          }
        }

        .index-content {
          flex-grow: 1;
          padding: 0 0 0 8px;
          box-sizing: border-box;
        }
      }
    }
  }

  .footer {
    position: absolute;
    right: 0;
    bottom: 0;
    left: 0;
    display: flex;
    justify-content: space-between;
    color: #9b9b9b;
    padding: 16px 24px 16px 64px;
    font-size: 0.8em;

    .focus {
      opacity: 0;

      &.show {
        opacity: 1;
      }
    }

    .right-container {
      display: flex;
      justify-content: flex-end;

      .logo-line {
        display: flex;
        justify-content: flex-end;
        align-items: center;

        .jll-logo {
          width: 64px;
          height: 24px;
          margin: 0;

          background-image: url('./../../assets/JLLLogo-Color.png');
          background-repeat: no-repeat;
          background-position: center right;
          background-size: cover;
        }

        .divider-vertical {
          width: 1px;
          height: 16px;
          border-radius: 2px;
          margin: 2px 16px -2px 12px;
          background: #9b9b9b;
        }

        .commuter-logo {
          font-size: 20px;
          color: colors.$stone-color;
          font-family: 'Golden Plains';
        }
      }
    }

    .page-number {
      text-align: right;
      width: 32px;
      color: #3c3c3b;
    }
  }
}
</style>
